import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  PdfRedactionArea,
  RedactionDocument,
  RedactionFootnote,
} from "../../db/schema";
import RedactionManager from "./RedactionManager";
import RedactionUpload from "./RedactionUpload";
import RedactionEditor from "./RedactionEditor";
import { pickDroppedPdf } from "../Forms/pickDroppedPdf";
import { REDACTIONS_CHANGED_EVENT } from "../../services/redactionUploadService";

/**
 * Top-level router for /redactions/* routes.
 * Fetches the user's redaction documents once and passes them down.
 */
const RedactionsRouter: React.FC = () => {
  const [documents, setDocuments] = useState<RedactionDocument[]>([]);
  const [loading, setLoading] = useState(true);
  // Mounted outside <Routes> so switching between /redactions and
  // /redactions/upload never unmounts the open dialog.
  const uploadModalRef = useRef<HTMLDialogElement>(
    null
  ) as React.RefObject<HTMLDialogElement>;
  const openUpload = useCallback(() => uploadModalRef.current?.showModal(), []);
  // A PDF dropped on the list: open the modal with it loaded.
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const handleFilesDropped = useCallback((files: File[]) => {
    const pdf = pickDroppedPdf(files, "to redact");
    if (!pdf) return;
    setDroppedFile(pdf);
    uploadModalRef.current?.showModal();
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get("/api/redaction-documents");
      setDocuments(response.data || []);
    } catch (error) {
      console.error("Error fetching redaction documents:", error);
      toast.error("Failed to fetch redaction documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // While any document is being identified server-side, poll for status
  // changes so the list flips from "processing" to "ready" on its own.
  useEffect(() => {
    const anyProcessing = documents.some(
      (d) => d.is_active && d.status === "processing"
    );
    if (!anyProcessing) return;
    const t = setInterval(fetchDocuments, 4000);
    return () => clearInterval(t);
  }, [documents, fetchDocuments]);

  // Uploads run in the background (tray): show each document in the list as it
  // is created, and again as the server finishes with it.
  useEffect(() => {
    window.addEventListener(REDACTIONS_CHANGED_EVENT, fetchDocuments);
    return () => window.removeEventListener(REDACTIONS_CHANGED_EVENT, fetchDocuments);
  }, [fetchDocuments]);

  return (
    <>
    <Routes>
      <Route
        index
        element={
          <RedactionManager
            documents={documents}
            loading={loading}
            onRefresh={fetchDocuments}
            onUploadClick={openUpload}
            onFilesDropped={handleFilesDropped}
          />
        }
      />
      <Route
        path="upload"
        element={
          <RedactionManager
            documents={documents}
            loading={loading}
            onRefresh={fetchDocuments}
            onUploadClick={openUpload}
            onFilesDropped={handleFilesDropped}
            autoOpenUpload
          />
        }
      />
      <Route
        path=":documentId"
        element={
          <EditorRoute documents={documents} onRefresh={fetchDocuments} />
        }
      />
    </Routes>
    <RedactionUpload modalRef={uploadModalRef} droppedFile={droppedFile} />
    </>
  );
};

// --- Route wrapper components ---

function EditorRoute({
  documents,
  onRefresh,
}: {
  documents: RedactionDocument[];
  onRefresh: () => Promise<void>;
}) {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState<RedactionDocument | undefined>(
    documents.find((d) => d.id === documentId)
  );

  useEffect(() => {
    const found = documents.find((d) => d.id === documentId);
    if (found) setDocument(found);
  }, [documents, documentId]);

  if (!document) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-base-300 rounded w-1/4"></div>
          <div className="h-32 bg-base-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <RedactionEditor
      document={document}
      onSave={async (
        redactions: PdfRedactionArea[],
        footnotes: RedactionFootnote[]
      ) => {
        const { data } = await axios.patch(
          `/api/redaction-documents/${document.id}`,
          { redactions, footnotes }
        );
        setDocument(data);
        await onRefresh();
      }}
      onCancel={() => navigate("/redactions")}
    />
  );
}

export default RedactionsRouter;
